import SessionStorage from "./SessionStorage";
import Cookies from "js-cookie";
import { isBrowser } from "../utils";

const LOCALE_BY_LANG = {
  en: "en_gb",
  es: "es_mx",
  fr: "fr_fr",
  pt: "pt_pt",
  ru: "ru_ru",
};

const getGtag = () => {
  if (isBrowser()) {
    return window.gtag;
  }
};

class Analytics {
  consentCookieName: string = null;
  gtag = null;

  constructor({ consentCookieName = null, gtag = getGtag() } = {}) {
    this.consentCookieName = consentCookieName;
    this.gtag = gtag;
  }

  /**
   * Record gtag event
   * @param eventName
   * @param params
   */
  recordEvent(eventName: string, params: Record<string, any> = {}) {
    const gtag = this.gtag ?? getGtag();

    if (!this.analyticsConsentGiven()) {
      console.debug("Consent not given to record event", eventName, params);
      return;
    }

    if (gtag) {
      gtag("event", eventName, JSON.parse(JSON.stringify(params)));
    } else {
      console.debug(
        "window.gtag() not found to record event",
        eventName,
        params
      );
    }
  }

  /**
   * Record page view event
   */
  recordPageView(pageTitle: string = window?.document?.title) {
    this.recordEvent("page_view", {
      page_location: window.location.href,
      language: this.getLanguage(),
      page_title: pageTitle,
      user_agent: navigator.userAgent,
    });
  }

  getLanguage(): string {
    const languagePrefix = SessionStorage.get("lang") || "en";

    return LOCALE_BY_LANG[languagePrefix] || LOCALE_BY_LANG.en;
  }

  /**
   * Returns if if user has given consent for analytics to be captured
   */
  analyticsConsentGiven() {
    const { consentCookieName } = this;
    const cookieConsent = consentCookieName && Cookies.get(consentCookieName);

    return cookieConsent === "true";
  }
}

let lastRoute: { href: string; key?: string };

/**
 * Call on each history change. Returns false while the URL and history entry
 * are the ones last recorded (e.g. Back onto an entry that only
 * redux-first-router navigated away from); otherwise records them and sends a
 * page_view. Mirrors Gatsby's RouteUpdates and gatsby-plugin-google-gtag,
 * whose page_views are not consent-gated.
 */
export const trackRouteChange = (): boolean => {
  const { href, pathname, search, hash } = window.location;
  const key = window.history.state?.key;

  if (lastRoute?.href === href && lastRoute?.key === key) {
    return false;
  }
  lastRoute = { href, key };

  if (
    process.env.NODE_ENV === "production" &&
    typeof window.gtag === "function"
  ) {
    // two frames later, so react-helmet has updated the title
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        window.gtag("event", "page_view", {
          page_path: pathname + search + hash,
        })
      )
    );
  }

  return true;
};

export default Analytics;
